import { useEffect, useMemo, useState } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

// Which ship-tos may this user order to? (v1.41.0)
//
// Assigning nothing is the normal state and means "every address", which is how
// every user behaved before this existed. The banner says so out loud, because
// a screen full of unchecked boxes otherwise reads as "this user can ship
// nowhere" — the opposite of what is true.
//
// Built for Colonial's 344 locations, so it filters rather than scrolling, and
// select-all acts on what the filter is showing, never on the whole list.
export default function ShipToAssigner({ userId, label }) {
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    api.get(`/api/addresses/users/${userId}/ship-tos`)
      .then(r => {
        if (cancelled) return;
        setRows(r.data.addresses || []);
        setPicked(new Set((r.data.addresses || []).filter(a => a.assigned).map(a => a.id)));
      })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [userId]);

  const shown = useMemo(() => {
    if (!rows) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(a => `${a.nickname} ${a.address1 || ''} ${a.city || ''}`.toLowerCase().includes(needle));
  }, [rows, q]);

  const toggle = (id) => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const selectShown = () => setPicked(prev => {
    const next = new Set(prev);
    shown.forEach(a => next.add(a.id));
    return next;
  });

  const clearShown = () => setPicked(prev => {
    const next = new Set(prev);
    shown.forEach(a => next.delete(a.id));
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/addresses/users/${userId}/ship-tos`, { address_ids: [...picked] });
      toast.success(picked.size ? `Assigned ${picked.size} ship-to${picked.size === 1 ? '' : 's'}` : 'Cleared — this user can ship to every address');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  if (rows === null) return null;

  return (
    <div style={{ borderTop: '1px solid var(--gray-200)', marginTop: 10, paddingTop: 10 }}>
      <strong>{label || 'Ship-to addresses'}</strong>
      <p className="form-hint" style={{ marginTop: 2 }}>
        {picked.size === 0
          ? 'Nothing assigned — this user can ship to every address on the customer. Tick addresses to restrict them to just those.'
          : `This user can only ship to the ${picked.size} ticked address${picked.size === 1 ? '' : 'es'}. Untick them all to allow every address again.`}
      </p>

      {rows.length === 0 ? (
        <p className="form-hint">This customer has no ship-to addresses yet.</p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input className="form-input" placeholder={`Filter ${rows.length} addresses…`}
              value={q} onChange={e => setQ(e.target.value)} />
            <button type="button" className="btn btn-secondary btn-sm" onClick={selectShown}>Select shown</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={clearShown}>Clear shown</button>
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 4, padding: 6 }}>
            {shown.length === 0 && <p className="form-hint" style={{ margin: 4 }}>No address matches that filter.</p>}
            {shown.map(a => (
              <label key={a.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '2px 2px' }}>
                <input type="checkbox" checked={picked.has(a.id)} onChange={() => toggle(a.id)} />
                <span>
                  {a.nickname}
                  {!a.active && <span className="badge badge-inactive" style={{ marginLeft: 6 }}>Inactive</span>}
                  {(a.city || a.address1) && (
                    <span className="form-hint" style={{ marginLeft: 6 }}>
                      {[a.address1, a.city, a.state].filter(Boolean).join(', ')}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save ship-tos'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
